<template>
  <el-scrollbar>
    <div class="home-box">
      <div class="chart-card meter-chart">
        <div :ref="(el) => (activity.chartRef = el)" class="chart-box"></div>
      </div>

      <div class="chart-card bar-chart activity-chart">
        <div class="chart-toolbar">
          <span class="chart-toolbar-label">公司：</span>
          <my-select v-model="activityGroupByDate.params.companyId" :options="companyOptions" clearable placeholder="所有公司" :style="{ width: '250px' }" />
        </div>
        <div :ref="(el) => (activityGroupByDate.chartRef = el)" class="chart-box"></div>
      </div>

      <div class="chart-card bar-chart activity-chart">
        <div class="chart-toolbar">
          <span class="chart-toolbar-label">公司：</span>
          <my-select v-model="activityGroupByMonth.params.companyId" :options="companyOptions" clearable placeholder="所有公司" :style="{ width: '250px' }" />
        </div>
        <div :ref="(el) => (activityGroupByMonth.chartRef = el)" class="chart-box"></div>
      </div>

      <div class="chart-card bar-chart is-horizontal">
        <div :ref="(el) => (topActivityGroupByCompany.chartRef = el)" class="chart-box"></div>
      </div>
    </div>
  </el-scrollbar>
</template>

<script setup>
import { reactive, onMounted, ref, watch } from 'vue'
import { newRequest } from '@/util/http'
import * as echarts from 'echarts'

const companyOptions = ref([])

const activity = reactive({
  chartRef: null,
  chart: null,
  params: { days: 30 },
  data: 0
})

const activityGroupByDate = reactive({
  chartRef: null,
  chart: null,
  params: { days: 30, companyId: null },
  data: []
})

const activityGroupByMonth = reactive({
  chartRef: null,
  chart: null,
  params: { months: 6, companyId: null },
  data: []
})

const topActivityGroupByCompany = reactive({
  chartRef: null,
  chart: null,
  params: { days: 30, limit: 10 },
  data: []
})

onMounted(() => {
  ;[activityGroupByMonth, activityGroupByDate, activity, topActivityGroupByCompany].forEach((it) => {
    if (it.chartRef) {
      it.chart = echarts.init(it.chartRef, 'light')
    }
  })

  loadHomeModel()
})

watch(
  () => activityGroupByDate.params.companyId,
  () => {
    loadActivityGroupByDate()
  }
)

watch(
  () => activityGroupByMonth.params.companyId,
  () => {
    loadActivityGroupByMonth()
  }
)

function loadHomeModel() {
  getAggregateData(
    {
      companies: {},
      activity: { days: activity.params.days },
      activityGroupByDate: { ...activityGroupByDate.params },
      activityGroupByMonth: { ...activityGroupByMonth.params },
      topActivityGroupByCompany: { ...topActivityGroupByCompany.params }
    },
    (model) => {
      companyOptions.value = (model.companies ?? []).map((company) => ({
        id: company.id,
        name: company.name
      }))

      const barChartRenderers = [
        {
          state: activityGroupByMonth,
          dataKey: 'activityGroupByMonth',
          title: `近${activityGroupByMonth.params.months}月活跃度变化`,
          horizontal: false
        },
        {
          state: activityGroupByDate,
          dataKey: 'activityGroupByDate',
          title: `近${activityGroupByDate.params.days}日活跃度变化`,
          horizontal: false
        },
        {
          state: topActivityGroupByCompany,
          dataKey: 'topActivityGroupByCompany',
          title: `近${topActivityGroupByCompany.params.days}日活跃度排名前${topActivityGroupByCompany.params.limit}的公司`,
          horizontal: true
        }
      ]

      barChartRenderers.forEach((renderer) => {
        const list = model[renderer.dataKey] ?? []
        renderer.state.data = list
        renderBarChart(
          renderer.state.chart,
          renderer.title,
          list.map((item) => item.key),
          list.map((item) => item.value),
          renderer.horizontal
        )
      })

      activity.data = model.activity ?? 0
      renderGaugeChart(activity.chart, `近${activity.params.days}日日均活跃度`, (activity.data / activity.params.days).toFixed(2))
    }
  )
}

function loadActivityGroupByDate() {
  if (!activityGroupByDate.chart) return
  const days = activityGroupByDate.params.days

  activityGroupByDate.chart.showLoading()
  getAggregateData(
    { activityGroupByDate: { ...activityGroupByDate.params } },
    (dataMap) => {
      const data = dataMap.activityGroupByDate ?? []
      activityGroupByDate.data = data
      renderBarChart(
        activityGroupByDate.chart,
        `近${days}日活跃度变化`,
        data.map((item) => item.key),
        data.map((item) => item.value)
      )
    },
    () => activityGroupByDate.chart.hideLoading()
  )
}

function loadActivityGroupByMonth() {
  if (!activityGroupByMonth.chart) return
  const months = activityGroupByMonth.params.months

  activityGroupByMonth.chart.showLoading()
  getAggregateData(
    { activityGroupByMonth: { ...activityGroupByMonth.params } },
    (dataMap) => {
      const data = dataMap.activityGroupByMonth ?? []
      activityGroupByMonth.data = data
      renderBarChart(
        activityGroupByMonth.chart,
        `近${months}月活跃度变化`,
        data.map((item) => item.key),
        data.map((item) => item.value)
      )
    },
    () => activityGroupByMonth.chart.hideLoading()
  )
}

function getAggregateData(body, success, complete) {
  newRequest({
    url: 'report/aggregate',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body,
    loading: false,
    success,
    complete
  })
}

function renderBarChart(chart, title, labels, data, horizontal = false) {
  const xAxis = {
    type: 'category',
    data: labels,
    axisLabel: {
      rotate: labels.length > 8 ? 45 : 0,
      interval: 0
    }
  }
  const yAxis = {
    type: 'value',
    axisLine: {
      show: true
    },
    splitLine: {
      lineStyle: {
        type: 'dashed'
      }
    }
  }
  chart.setOption({
    title: {
      text: title,
      textStyle: {
        color: '#333',
        fontSize: 16,
        fontWeight: 500
      },
      left: 'center',
      top: '5%'
    },
    grid: {
      top: '65px',
      bottom: '25px',
      left: '40px',
      right: '40px',
      containLabel: true
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    xAxis: horizontal ? yAxis : xAxis,
    yAxis: horizontal ? xAxis : yAxis,
    series: [
      {
        data,
        type: 'bar',
        barWidth: '60%',
        label: {
          show: true,
          position: 'top',
          color: '#666'
        },
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#5470c6' },
            { offset: 1, color: '#91cc75' }
          ]),
          borderRadius: [4, 4, 0, 0]
        }
      }
    ]
  })
}

function renderGaugeChart(chart, title, value) {
  chart.setOption({
    series: [
      {
        name: title,
        type: 'gauge',
        detail: {
          formatter: '{value}'
        },
        data: [
          {
            value: value,
            name: title
          }
        ]
      }
    ]
  })
}
</script>

<style lang="scss" scoped>
.home-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 20px;
  box-sizing: border-box;
  background: #f5f7fa;
}

.chart-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.12);
  }
}

.meter-chart {
  width: 480px;
  height: 480px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bar-chart {
  width: 900px;
  height: 400px;

  &.is-horizontal {
    min-height: 400px;
  }
}

.chart-box {
  width: 100%;
  height: 100%;
}

.activity-chart {
  display: flex;
  flex-direction: column;
}

.activity-chart .chart-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
}

.activity-chart .chart-toolbar-label {
  color: #666;
  font-size: 13px;
}

.activity-chart .chart-box {
  height: auto;
  flex: 1;
  min-height: 0;
}
</style>
